import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useEffect, useRef, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';

import {
  useAllObjectClassListQuery,
  useCreateObjectClassMutation,
  useReorderExtendClassMutation,
  useSuperObjectClassListQuery,
  useUpdateObjectClassMutation,
} from '@/hook';
import {
  Close,
  Delete,
  Done,
  DragHandle,
  KeyboardArrowDown,
  VBasePopover,
  VDialog,
  VFlex,
  VIconButton,
  VInput,
  VStack,
  VSwitch,
  VToggleButton,
  cn,
  useUnleash,
} from '@/package';
import type { NewExtendClassItemFormType, ObjectClass } from '@/type';

import IconPopover from './IconPopover';
import { SuperClassItem } from './ObjectClass';

const DEFAULT_ICON_COLOR = '#349af9';

const matchesSearchTerm = (item: ObjectClass, searchTerm: string): boolean => {
  if (!searchTerm) return true;

  const lowerSearchTerm = searchTerm.toLowerCase();
  return (
    item.alias?.toLowerCase().includes(lowerSearchTerm) ||
    item.name.toLowerCase().includes(lowerSearchTerm) ||
    item.memo.toLowerCase().includes(lowerSearchTerm)
  );
};

const ObjectClassManagementModal = ({
  visible,
  onUpdateVisible,
}: {
  visible: boolean;
  onUpdateVisible(newVisible: boolean): void;
}) => {
  const { data: superClassList } = useSuperObjectClassListQuery();

  const [searchTerm, setSearchTerm] = useState('');
  const [isHideInactive, setIsHideInactive] = useState(false);

  return (
    <VDialog
      visible={visible}
      onUpdateVisible={onUpdateVisible}
      dialogMainClass="w-[824px] h-[657px]"
    >
      <VStack className="size-full bg-white p-6" gap={1}>
        <VFlex items="center" justify="between">
          <div className="text-headline-md font-bold">
            Object class management
          </div>
          <Close
            className="size-4 text-gray-400"
            role="button"
            onClick={() => onUpdateVisible(false)}
          />
        </VFlex>

        <VStack className="flex-1 overflow-y-hidden" gap={0.5}>
          <ObjectClassManagementHeader
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            isHideInactive={isHideInactive}
            setIsHideInactive={setIsHideInactive}
            superClassList={superClassList ?? []}
          />
          <ObjectClassManagementTable
            searchTerm={searchTerm}
            superClassList={superClassList ?? []}
            isHideInactive={isHideInactive}
          />
        </VStack>
      </VStack>
    </VDialog>
  );
};

const ObjectClassManagementHeader = ({
  searchTerm,
  setSearchTerm,
  isHideInactive,
  setIsHideInactive,
  superClassList,
}: {
  searchTerm: string;
  setSearchTerm: (searchTerm: string) => void;
  isHideInactive: boolean;
  setIsHideInactive: (isHideInactive: boolean) => void;
  superClassList: ObjectClass[];
}) => {
  const { getVariantPayloadValue } = useUnleash();
  const payload = getVariantPayloadValue<{
    activeLimitCount: number;
  }>('vuex.etxendClass.activeLimit');
  const activeClassListLength = (
    superClassList?.filter(item => item.enable) ?? []
  ).length;
  const totalClassListLength = superClassList?.length ?? 0;
  const activeLimitCount = payload?.activeLimitCount ?? 0;

  return (
    <VFlex items="center" justify="between">
      <VFlex gap={0.5} items="center">
        <VFlex gap={0.25} items="center" className="text-body-lg text-gray-500">
          <span>Total</span>
          <span>{totalClassListLength}</span>
        </VFlex>

        <div className="h-4 w-px bg-gray-300" />

        <VFlex gap={0.25} items="center" className="text-body-lg text-blue-300">
          <span>Active</span>
          <span>
            {activeClassListLength}/{activeLimitCount}
          </span>
        </VFlex>
      </VFlex>

      <VFlex items="center" gap={0.5}>
        <VToggleButton
          size="sm"
          isSelected={isHideInactive}
          onClick={() => {
            setIsHideInactive(!isHideInactive);
          }}
        >
          Hide inactive
        </VToggleButton>
        <VInput
          className="w-44"
          placeholder="Search"
          type="search"
          size="sm"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
      </VFlex>
    </VFlex>
  );
};

const ObjectClassManagementTable = ({
  searchTerm,
  superClassList,
  isHideInactive,
}: {
  searchTerm: string;
  superClassList: ObjectClass[];
  isHideInactive: boolean;
}) => {
  const filteredSuperClassList = superClassList.filter(
    superClass =>
      matchesSearchTerm(superClass, searchTerm) ||
      superClass.extendClass?.some(extendClass =>
        matchesSearchTerm(extendClass, searchTerm),
      ),
  );

  return (
    <VStack className="max-h-full w-full divide-y divide-gray-300 overflow-y-auto rounded-md border border-gray-300">
      <ObjectClassManagementTableHeader />
      {filteredSuperClassList.map(superClass => (
        <ObjectClassManagementTableRow
          key={superClass.id}
          superClass={superClass}
          searchTerm={searchTerm}
          isHideInactive={isHideInactive}
        />
      ))}
    </VStack>
  );
};

const ObjectClassManagementTableHeader = () => {
  return (
    <VFlex className="w-full divide-x divide-gray-300 text-body-md font-bold text-gray-400">
      <div className="w-8 p-2">
        <KeyboardArrowDown className="size-4 text-gray-400" />
      </div>
      <div className="w-10 p-2">Icon</div>
      <div className="w-[184px] p-2">Name</div>
      <div className="w-[231px] p-2">Size (m)</div>
      <div className="flex-1 p-2">Memo</div>
      <div className="w-[72px] p-2">Enabled</div>
    </VFlex>
  );
};

const ObjectClassManagementTableRow = ({
  superClass,
  searchTerm,
  isHideInactive,
}: {
  superClass: ObjectClass;
  searchTerm: string;
  isHideInactive: boolean;
}) => {
  const { extendClass } = superClass;
  const filteredExtendClass =
    extendClass?.filter(
      item => !isHideInactive || (isHideInactive && item.enable),
    ) ?? [];
  const searchedExtendClass = filteredExtendClass.filter(item =>
    matchesSearchTerm(item, searchTerm),
  );

  const [isExtendClassOpen, setIsExtendClassOpen] = useState(false);
  const [isNewExtendClassItemFormOpen, setIsNewExtendClassItemFormOpen] =
    useState(false);

  const { mutate: reorderExtendClass } = useReorderExtendClassMutation();

  useEffect(() => {
    if (!searchTerm) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsExtendClassOpen(searchedExtendClass.length ? true : false);
  }, [searchTerm]);

  const handleReorder = (
    newOrder: ObjectClass[],
    activeID: ObjectClass['id'],
  ) => {
    const prevID = (() => {
      const index = newOrder.findIndex(item => item.id === activeID);
      if (index === 0) return Number(newOrder[index].parentID);
      return newOrder[index - 1].id;
    })();

    const nextID = (() => {
      const index = newOrder.findIndex(item => item.id === activeID);
      if (index === newOrder.length - 1) return 0;
      return newOrder[index + 1].id;
    })();

    reorderExtendClass({
      pObjectClassID: activeID,
      prevID: String(prevID),
      nextID: nextID ? String(nextID) : null,
    });
  };

  return (
    <VFlex className="w-full divide-x divide-gray-300 text-body-md text-black">
      <VStack className="flex-1">
        <SuperClassItem
          superClass={superClass}
          isExtendClassOpen={isExtendClassOpen}
          setIsExtendClassOpen={setIsExtendClassOpen}
          setIsNewExtendClassItemFormOpen={setIsNewExtendClassItemFormOpen}
        />
        {isNewExtendClassItemFormOpen && (
          <NewExtendClassItemForm
            onClose={() => setIsNewExtendClassItemFormOpen(false)}
            superClass={superClass}
          />
        )}
        {isExtendClassOpen && (
          <ObjectClassItemList
            extendClass={searchedExtendClass}
            onReorder={handleReorder}
          />
        )}
      </VStack>
    </VFlex>
  );
};

const ObjectClassItemList = ({
  extendClass,
  onReorder,
}: {
  extendClass: ObjectClass[];
  onReorder: (newOrder: ObjectClass[], activeID: ObjectClass['id']) => void;
}) => {
  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = extendClass.findIndex(item => item.id === active.id);
      const newIndex = extendClass.findIndex(item => item.id === over?.id);

      const newOrder = arrayMove(extendClass, oldIndex, newIndex);
      onReorder(newOrder, Number(active.id));
    }
  };

  return (
    <VStack className="w-full">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      >
        <SortableContext
          items={extendClass.map(item => item.id)}
          strategy={verticalListSortingStrategy}
        >
          {extendClass.map(extendClassItem => (
            <SortableExtendClassItem
              key={extendClassItem.id}
              extendClass={extendClassItem}
            />
          ))}
        </SortableContext>
      </DndContext>
    </VStack>
  );
};

const NewExtendClassItemForm = ({
  onClose,
  superClass,
}: {
  onClose: () => void;
  superClass: ObjectClass;
}) => {
  const { data: allClassList } = useAllObjectClassListQuery();
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    setValue,
  } = useForm<NewExtendClassItemFormType>({
    defaultValues: {
      name: '',
      size: [6, 2.5, 2.5],
      memo: '',
      color: DEFAULT_ICON_COLOR,
      iconID: Number(superClass.icon?.id ?? 1),
    },
    mode: 'onChange',
  });
  const [isNameDuplicate, setIsNameDuplicate] = useState(false);
  const { mutate: createObjectClass } = useCreateObjectClassMutation();

  const validateName = (value: string) => {
    const isEmpty = value.trim() === '';
    if (isEmpty) return 'Name is required';

    const isDuplicate = allClassList?.some(item => item.name === value);
    setIsNameDuplicate(!!isDuplicate);
    return isDuplicate ? 'Name is already in use' : true;
  };

  const onSubmit: SubmitHandler<NewExtendClassItemFormType> = formData => {
    createObjectClass(
      {
        name: formData.name,
        color: formData.color,
        size: formData.size,
        iconID: Number(formData.iconID),
        parentID: superClass.id,
        classType: 'Extend',
        memo: formData.memo,
      },
      {
        onSuccess: () => {
          onClose();
        },
      },
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <VFlex className="relative h-full w-full divide-x divide-gray-300 text-body-md text-black">
        <VFlex className="h-full w-8 border-y-0 p-2" justify="center" />

        <VFlex
          className="w-10 border-t border-gray-300"
          items="center"
          justify="center"
        >
          <IconPopover
            iconID={watch('iconID')}
            color={watch('color')}
            onColorChange={value => setValue('color', value)}
            onIconClick={iconID => {
              setValue('iconID', Number(iconID));
            }}
            onResetClick={() => {
              setValue('color', DEFAULT_ICON_COLOR);
              setValue('iconID', Number(superClass.icon?.id ?? 1));
            }}
          />
        </VFlex>
        <VFlex
          className="w-[184px] border-t border-gray-300 px-2"
          items="center"
          justify="between"
        >
          <VBasePopover
            visible={isNameDuplicate}
            trigger="focus"
            placement="bottom"
            content={() =>
              isNameDuplicate ? (
                <div className="rounded-sm bg-black px-2 py-1 text-body-md text-error-500">
                  {errors.name?.message}
                </div>
              ) : null
            }
          >
            <VInput
              size="sm"
              orientation="horizontal"
              placeholder="New class"
              {...register('name', { validate: validateName })}
              valid={!errors.name}
            />
          </VBasePopover>
        </VFlex>
        <VFlex
          className="w-[231px] border-t border-gray-300 px-2 py-1"
          gap={0.25}
          items="center"
          justify="center"
        >
          <VInput
            size="sm"
            orientation="horizontal"
            fluid
            label="L"
            {...register('size.0', {
              required: 'Length is required',
              min: {
                value: 1,
                message: 'Length must be at least 1',
              },
              valueAsNumber: true,
            })}
          />
          <VInput
            size="sm"
            orientation="horizontal"
            fluid
            label="W"
            {...register('size.1', {
              required: 'Width is required',
              min: {
                value: 1,
                message: 'Width must be at least 1',
              },
              valueAsNumber: true,
            })}
          />
          <VInput
            size="sm"
            orientation="horizontal"
            fluid
            label="D"
            {...register('size.2', {
              required: 'Depth is required',
              min: {
                value: 1,
                message: 'Depth must be at least 1',
              },
              valueAsNumber: true,
            })}
          />
        </VFlex>
        <VFlex
          className="flex-1 border-t border-gray-300 px-2 py-1"
          items="center"
          justify="center"
        >
          <VInput
            size="sm"
            orientation="horizontal"
            fluid
            placeholder="Enter a memo"
            {...register('memo')}
          />
        </VFlex>
        <VFlex
          className="w-[72px] border-t border-gray-300"
          items="center"
          justify="center"
          gap={0.25}
        >
          <VIconButton
            Icon={Done}
            disabled={!isValid}
            size="sm"
            type="submit"
          />
          <VIconButton
            type="button"
            Icon={Delete}
            variant="danger"
            size="sm"
            onClick={onClose}
          />
        </VFlex>
      </VFlex>
    </form>
  );
};

const SortableExtendClassItem = ({
  extendClass,
}: {
  extendClass: ObjectClass;
}) => {
  const {
    register,
    setValue,
    watch,
    getValues,
    formState: { errors, isValid },
    trigger,
  } = useForm<Omit<ObjectClass, 'icon' | 'iconID'> & { iconID: number }>({
    defaultValues: {
      ...extendClass,
      iconID: Number(extendClass.icon?.id ?? 1),
    },
    mode: 'onChange',
  });
  const { data: allClassList } = useAllObjectClassListQuery();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: extendClass.id });
  const [isHover, setIsHover] = useState(false);
  const [isNameDuplicate, setIsNameDuplicate] = useState(false);
  const { mutate: updateObjectClass } = useUpdateObjectClassMutation();
  const orgValue = useRef<{ color: string; iconID: number }>({
    color: extendClass.color,
    iconID: Number(extendClass.icon?.id ?? 1),
  });

  const style = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const onValueChangeEnd = () => {
    if (!isValid) return;
    updateObjectClass({
      pObjectClassID: extendClass.id,
      ...getValues(),
      iconID: Number(getValues('iconID')),
    });
  };

  const validateName = (value: string) => {
    const isEmpty = value.trim() === '';
    if (isEmpty) return 'Name is required';

    const isDuplicate = allClassList?.some(
      item => item.id !== extendClass.id && item.name === value,
    );
    setIsNameDuplicate(!!isDuplicate);
    return isDuplicate ? 'Name is already in use' : true;
  };

  return (
    <VFlex
      ref={setNodeRef}
      style={style}
      className={
        'relative h-full w-full divide-x divide-gray-300 text-body-md text-black hover:shadow-sm'
      }
    >
      <VFlex
        className={cn('h-full w-8 border-y-0 p-2', {
          'bg-skyBlue-200': isDragging,
        })}
        justify="center"
        {...attributes}
        {...listeners}
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
      >
        {isHover && (
          <DragHandle
            className={cn('size-4 text-primary-500')}
            style={{
              display: 'hidden',
            }}
          />
        )}
      </VFlex>

      <VFlex
        className="w-10 border-t border-gray-300"
        items="center"
        justify="center"
      >
        <IconPopover
          iconID={watch('iconID')}
          color={watch('color')}
          onColorChange={value => {
            setValue('color', value);
            onValueChangeEnd();
          }}
          onIconClick={iconID => {
            setValue('iconID', Number(iconID));
            onValueChangeEnd();
          }}
          onResetClick={() => {
            setValue('color', orgValue.current.color);
            setValue('iconID', orgValue.current.iconID);
            onValueChangeEnd();
          }}
        />
      </VFlex>
      <VFlex
        className="w-[184px] border-t border-gray-300 px-2"
        items="center"
        justify="between"
      >
        <VBasePopover
          visible={isNameDuplicate}
          trigger="focus"
          placement="bottom"
          content={() =>
            isNameDuplicate ? (
              <div className="rounded-sm bg-black px-2 py-1 text-body-md text-error-500">
                {errors.name?.message}
              </div>
            ) : null
          }
        >
          <VInput
            size="sm"
            orientation="horizontal"
            {...register('name', { validate: validateName })}
            onBlur={_ => {
              if (errors.name) {
                setValue('name', extendClass.name);
                trigger('name');
                return;
              }
              onValueChangeEnd();
            }}
            valid={!errors.name}
          />
        </VBasePopover>
      </VFlex>
      <VFlex
        className="w-[231px] border-t border-gray-300 px-2 py-1"
        gap={0.25}
        items="center"
        justify="center"
      >
        {extendClass.size.map((size, index) => (
          <VInput
            key={`${size}-${index}`}
            size="sm"
            orientation="horizontal"
            fluid
            {...register(`size.${index}`, {
              required: `${index === 0 ? 'Length' : index === 1 ? 'Width' : 'Depth'} is required`,
              min: {
                value: 1,
                message: `${index === 0 ? 'Length' : index === 1 ? 'Width' : 'Depth'} must be at least 1`,
              },
              valueAsNumber: true,
            })}
            onBlur={_ => {
              if (errors.size?.[index]) {
                setValue(`size.${index}`, extendClass.size[index]);
                trigger(`size.${index}`);
                return;
              }
              onValueChangeEnd();
            }}
            label={index === 0 ? 'L' : index === 1 ? 'W' : 'D'}
            type="number"
            valid={!errors.size?.[index]}
            step={0.1}
          />
        ))}
      </VFlex>
      <VFlex
        className="flex-1 border-t border-gray-300 px-2 py-1"
        items="center"
        justify="center"
      >
        <VInput
          size="sm"
          orientation="horizontal"
          fluid
          {...register('memo')}
          onBlur={onValueChangeEnd}
          placeholder="Enter a memo"
        />
      </VFlex>
      <VFlex
        className="w-[72px] border-t border-gray-300"
        items="center"
        justify="center"
      >
        <VSwitch
          checked={watch('enable')}
          onChange={checked => {
            setValue('enable', checked);
            onValueChangeEnd();
          }}
        />
      </VFlex>
    </VFlex>
  );
};

export default ObjectClassManagementModal;
